import { useState, useEffect, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useAppData } from "../../../context/AppDataContext";
import { isValidEmail } from "../../../util/stringUtils";
import { errorToast, successfulToast } from "../../../util/toastNotifications";
import FormFieldText from "../../input/FormFieldText";
import FormTextArea from "../../input/FormTextArea";
import ContactComboBoxSelect from "../../input/ContactComboBoxSelect";
import Button from "../../core/Button";
import Form from "../../layout/Form";

const normalize = (v) => v.trim().toLowerCase();

const ContractForm = () => {
  const { api } = useAppData();
  const { contractId } = useParams();
  const isEdit = Boolean(contractId);
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showContactComboBox, setShowContactComboBox] = useState(false);

  const [formErrors, setFormErrors] = useState({});
  const [formFields, setFormFields] = useState({
    name: "",
    po_number: "",
    contract_number: "",
    company_id: "",
    contact_id: "",
    invoice_email: "",
    org_id: "",
    max_contract_amount: 0,
    contract_type: "",
    start_date: "",
    end_date: "",
    description: "",
    is_active: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [companiesData, contactsData] = await Promise.all([
          api.company.getCompanies(),
          api.contact.getContacts(),
        ]);
        setCompanies(companiesData);
        setContacts(contactsData);

        if (contractId) {
          const contractData = await api.contract.getContractById(contractId);
          const formatDateForInput = (dateString) => {
            if (!dateString) return "";
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
          };
          setFormFields({
            name: contractData.name,
            po_number: contractData.po_number,
            contract_number: contractData.contract_number,
            company_id: contractData.company_id,
            org_id: contractData.org_id,
            contact_id: contractData.contact_id,
            contract_type: contractData.contract_type,
            invoice_email: contractData.invoice_email,
            max_contract_amount: contractData.max_contract_amount,
            start_date: formatDateForInput(contractData.start_date),
            end_date: formatDateForInput(contractData.end_date),
            description: contractData.description || "",
          });
        }
      } catch (error) {
        console.error("Error loading contract form data:", error);
        errorToast("Failed to load contract form. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, contractId]);

  const orgIdForCompany = (companyId) => {
    const company = companies.find(
      (c) => String(c.company_id) === String(companyId)
    );
    return company?.org_id || "";
  };

  const organizationContacts = useMemo(() => {
    if (!formFields.org_id) return [];
    return contacts.filter(
      (contact) => String(contact.org_id) === String(formFields.org_id)
    );
  }, [contacts, formFields.org_id]);

  const handleFieldUpdate = (e) => {
    const { name, value } = e.target;
    if (name === "company_id") {
      setShowContactComboBox(false);
      setFormFields((prev) => ({
        ...prev,
        company_id: value,
        org_id: orgIdForCompany(value),
        contact_id: "",
      }));
      return;
    }
    setFormFields((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContactSelect = (contactId) => {
    setFormFields((prev) => ({
      ...prev,
      contact_id: contactId,
    }));
    setFormErrors((prev) => {
      if (!prev.contact) return prev;
      const { contact, ...rest } = prev;
      return rest;
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!formFields.name.trim()) {
      errors.name = "Name is required";
    }

    if (formFields.invoice_email) {
      if (!isValidEmail(formFields.invoice_email)) {
        errors.invoice_email = "Invalid email format";
      } else if (errors.invoice_email) {
        delete errors.invoice_email;
      }
    }

    if (formFields.start_date && formFields.end_date) {
      const startDate = new Date(formFields.start_date);
      const endDate = new Date(formFields.end_date);
      if (endDate < startDate) {
        errors.end_date = "End date must be after start date";
      }
    }

    if (formFields.max_contract_amount) {
      const maxAmount = parseFloat(formFields.max_contract_amount);
      if (isNaN(maxAmount) || maxAmount < 0) {
        errors.max_contract_amount =
          "Max amount estimate must be a positive number";
      }
    }

    if (!formFields.contact_id) {
      errors.contact = "Please select a Contact";
    }

    if (!formFields.company_id) {
      errors.company_id = "Please select a Company";
    } else if (!formFields.org_id) {
      errors.company_id =
        "Selected company does not have an organization";
    }

    if (!formFields.contract_type || formFields.contract_type == "unselected") {
      errors.contract_type = "Please select a Contract Type";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const name = normalize(formFields.name);
    if (!name) return;

    setLoading(true);

    try {
      const payload = {
        name: formFields.name.trim(),
        po_number: formFields.po_number,
        contract_number: formFields.contract_number,
        company_id: formFields.company_id ? formFields.company_id : null,
        contact_id: formFields.contact_id ? formFields.contact_id : null,
        invoice_email: formFields.invoice_email,
        org_id: formFields.org_id,
        max_contract_amount: formFields.max_contract_amount,
        contract_type: formFields.contract_type,
        start_date: formFields.start_date
          ? new Date(formFields.start_date + "T00:00:00").toISOString()
          : null,
        end_date: formFields.end_date
          ? new Date(formFields.end_date + "T00:00:00").toISOString()
          : null,
        description: formFields.description,
        is_active: true,
      };

      if (isEdit) {
        await api.contract.updateContract(contractId, payload);
        successfulToast("Contract updated successfully");
        history.push(`/contract/${contractId}`);
      } else {
        const newContract = await api.contract.createContract(payload);
        successfulToast("Contract created successfully");
        history.push(`/contract/${newContract.contract_id}`);
      }
    } catch (error) {
      console.error("Error saving contract:", error);
      errorToast(
        isEdit ? "Failed to update contract" : "Failed to create contract"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-styling px-16 py-6 card">
      <h2>{isEdit ? "Edit Contract" : "New Contract"}</h2>

      <Form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <FormFieldText
              name="name"
              label="Contract Name *"
              value={formFields.name}
              onChange={handleFieldUpdate}
            />
            {formErrors.name && (
              <div className="invalid-feedback">{formErrors.name}</div>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group col-md-6">
            <FormFieldText
              name="po_number"
              label="PO Number"
              value={formFields.po_number}
              onChange={handleFieldUpdate}
            />
          </div>
          <div className="form-group col-md-6">
            <FormFieldText
              name="contract_number"
              label="Contract Number"
              value={formFields.contract_number}
              onChange={handleFieldUpdate}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="company_id">Company *</label>
            <select
              id="company_id"
              name="company_id"
              value={formFields.company_id}
              onChange={handleFieldUpdate}
              className="form-control"
            >
              <option key="company-default" value="">
                Select a Company
              </option>
              {companies.map((company, index) => (
                <option
                  key={company.company_id || `company-${index}`}
                  value={company.company_id}
                >
                  {company.name}
                </option>
              ))}
            </select>
            {formErrors.company_id && (
              <div className="invalid-feedback">{formErrors.company_id}</div>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group col-md-6">
            <label htmlFor="contact_id">Contact Name *</label>
            <ContactComboBoxSelect
              triggerId="contact_id"
              contacts={organizationContacts}
              selectedContactId={formFields.contact_id}
              onSelect={handleContactSelect}
              showComboBox={showContactComboBox}
              onToggle={() => setShowContactComboBox((prev) => !prev)}
              onClose={() => setShowContactComboBox(false)}
              emptyLabel={
                formFields.company_id
                  ? "Select a Contact"
                  : "Select a company first"
              }
              disabled={!formFields.company_id}
              error={Boolean(formErrors.contact)}
            />
            {formErrors.contact && (
              <div className="invalid-feedback">{formErrors.contact}</div>
            )}
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="contract_type">Contract Type *</label>
            <select
              id="contract_type"
              name="contract_type"
              value={formFields.contract_type}
              onChange={handleFieldUpdate}
            >
              <option value="unselected">Select a Contract Type</option>
              <option value="fixed_price">Fixed Price</option>
              <option value="hourly">Hourly</option>
              <option value="monthly_retainer">Monthly Retainer</option>
              <option value="cost_plus">Cost Plus</option>
              <option value="incentive">Incentive</option>
              <option value="unit_price">Unit Price</option>
            </select>
            {formErrors.contract_type && (
              <div className="invalid-feedback col-md-6">
                {formErrors.contract_type}
              </div>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group col-md-6">
            <FormFieldText
              name="invoice_email"
              label="Invoice Email"
              value={formFields.invoice_email}
              onChange={handleFieldUpdate}
            />
            {formErrors.invoice_email && (
              <div className="invalid-feedback">{formErrors.invoice_email}</div>
            )}
          </div>
          <div className="form-group col-md-6">
            <FormFieldText
              name="max_contract_amount"
              label="Maximum Amount"
              type="number"
              value={formFields.max_contract_amount}
              onChange={handleFieldUpdate}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group col-md-6">
            <label htmlFor="start_date">Start Date</label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              value={formFields.start_date}
              onChange={handleFieldUpdate}
              className={formErrors.start_date ? "is-invalid" : ""}
            />
            {formErrors.start_date && (
              <div className="invalid-feedback">{formErrors.start_date}</div>
            )}
          </div>

          <div className="form-group col-md-6">
            <label htmlFor="end_date">End Date</label>
            <input
              type="date"
              id="end_date"
              name="end_date"
              value={formFields.end_date}
              onChange={handleFieldUpdate}
              className={formErrors.end_date ? "is-invalid" : ""}
            />
            {formErrors.end_date && (
              <div className="invalid-feedback">{formErrors.end_date}</div>
            )}
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <FormTextArea
              name="description"
              label="Description"
              value={formFields.description}
              onChange={handleFieldUpdate}
            />
          </div>
        </div>

        <div className="button-row m-0">
          <Button cancel onClick={() => history.goBack()}>
            Cancel
          </Button>
          <button type="submit" disabled={loading}>
            {isEdit ? "Update Contract" : "Create Contract"}
          </button>
        </div>
      </Form>
    </div>
  );
};

export default ContractForm;
